import { PROFESSIONAL_FIELDS } from './constants';

/**
 * Frontend mirror of the backend's ProfessionScopeService.
 *
 * A user holds a primary `professionType` plus a `professions` array with every
 * profession they are responsible for (a practice instructor may supervise e.g.
 * Informatik and KV apprentices). `ALL` means unrestricted - never confuse it
 * with an empty list, which means "nothing configured yet".
 */

/**
 * True when the user sees every profession.
 *
 * Driven by the stored profession, NOT by the ADMIN role: a Berufsbildner who
 * also holds ADMIN is still scoped to their own professions, and only someone
 * explicitly set to ALL is unrestricted.
 */
export const isUnrestricted = (user) => user?.professionType === 'ALL';

/**
 * The professions a user actually holds, as stored. An unrestricted user holds
 * none of them - they hold ALL - so this returns an empty list for them. Use
 * it for display; use professionOptionsFor for filter dropdowns.
 */
export const professionsOf = (user) => {
  const explicit = (user?.professions || []).filter((id) => id && id !== 'ALL');
  if (explicit.length > 0) {
    return explicit;
  }
  // Fallback for responses that only carry the primary profession.
  return user?.professionType && user.professionType !== 'ALL'
    ? [user.professionType]
    : [];
};

/**
 * What to show as badges: a single "ALL" entry for an unrestricted
 * user, otherwise one entry per profession they hold.
 */
export const displayProfessionsOf = (user) =>
  isUnrestricted(user) ? ['ALL'] : professionsOf(user);

/** True when the user is responsible for `professionType`. */
export const coversProfession = (user, professionType) => {
  if (!professionType) {
    return false;
  }
  if (isUnrestricted(user)) {
    return true;
  }
  return professionsOf(user).includes(professionType);
};

/** Filter helper: keeps the learners whose profession the user covers. */
export const filterByScope = (user, learners = []) => {
  if (isUnrestricted(user)) {
    return learners;
  }
  const scope = professionsOf(user);
  if (scope.length === 0) {
    return learners;
  }
  return learners.filter((learner) => scope.includes(learner?.professionType));
};

/**
 * The filter options a user may choose from, as {id, label} entries.
 * Unrestricted users may choose any profession.
 */
export const professionOptionsFor = (user) => {
  if (isUnrestricted(user)) {
    return PROFESSIONAL_FIELDS;
  }
  const scope = professionsOf(user);
  return PROFESSIONAL_FIELDS.filter((field) => scope.includes(field.id));
};

/** Human-readable list, e.g. "Informatik, KV". */
export const professionLabels = (professionIds = []) =>
  professionIds
    .map((id) =>
      id === 'ALL'
        ? 'ALL'
        : PROFESSIONAL_FIELDS.find((field) => field.id === id)?.label || id,
    )
    .join(', ');
